import { useEffect, useState } from "react";
import axios from "axios";
import * as Icons from "lucide-react";
import { PUBLIC_BASE } from "../lib/api";

/**
 * "X people viewing now" social-proof badge. Pings the heartbeat endpoint
 * every 25s and reads the live count every 30s. The endpoint counts distinct
 * visitor_ids that pinged within the last 90s — so this gracefully decays
 * after a viewer leaves.
 *
 * Usage:
 *   <LiveViewerBadge kind="dashboard" token={dashboardToken} brand={brand} />
 *   <LiveViewerBadge kind="reel"      token={shareToken}     brand={brand} />
 */
export default function LiveViewerBadge({ kind, token, brand, className = "" }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!token || !kind) return;
    const visitorId = ensureVisitorId();
    const base =
      kind === "dashboard"
        ? `${PUBLIC_BASE}/api/share/dashboards/${token}`
        : `${PUBLIC_BASE}/api/share/reels/${token}`;

    const beat = () => {
      axios.post(`${base}/heartbeat`, { visitor_id: visitorId }).catch(() => {});
    };
    const fetchCount = () => {
      axios
        .get(`${base}/live`)
        .then((r) => setCount(Number(r.data?.viewing_now || 0)))
        .catch(() => {});
    };

    beat();
    fetchCount();
    const beatId = setInterval(beat, 25000);
    const countId = setInterval(fetchCount, 30000);
    return () => {
      clearInterval(beatId);
      clearInterval(countId);
    };
  }, [kind, token]);

  if (count <= 0) return null;

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs ${className}`}
      style={brand ? { background: `${brand}15`, borderColor: `${brand}40`, color: brand } : undefined}
      data-testid="live-viewer-badge"
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
      </span>
      <span className="font-medium">
        {count === 1 ? "1 person viewing now" : `${count} people viewing now`}
      </span>
    </div>
  );
}

function ensureVisitorId() {
  let v = null;
  try {
    v = localStorage.getItem("pm_visitor_id");
    if (!v) {
      v = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("pm_visitor_id", v);
    }
  } catch {
    v = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return v;
}
